/**
 * First-touch attribution: the `utm_*` tags and referrer of the very first
 * visit, remembered so they can ride the person profile at signup.
 *
 * BROWSER ONLY (reads `window`/`document`/localStorage). Re-exported from
 * `./client`, never from the isomorphic barrel in `index.ts`.
 *
 * Why this exists: a campaign link lands on `/?utm_source=…`, but signup
 * happens screens (and an OTP round trip) later, after the router has moved
 * on and the query string is gone. The signup event itself is emitted by the
 * API, which never sees the browser URL. So the tags are captured once at app
 * start, sent to the API in the account-creating request (which stamps them on
 * the signup event), and set as `first_*` person properties on identify.
 *
 * Ground rule 1: analytics is never load-bearing. Nothing here throws, and
 * every storage access is guarded.
 */

import { FIRST_TOUCH_KEYS, FIRST_TOUCH_MAX_LENGTH } from './properties';
import type { FirstTouch } from './properties';

/** Namespaced like the app-side keys in `lib/analytics.ts` (`tupai_signup_at`). */
export const FIRST_TOUCH_STORAGE_KEY = 'tupai_analytics_first_touch';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

function clean(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, FIRST_TOUCH_MAX_LENGTH) : undefined;
}

/**
 * Capacitor injects `window.Capacitor` into the native WebView. Read off
 * `window` rather than importing `@capacitor/core`, which this package does
 * not depend on. A native WebView has no web referrer and no campaign query,
 * so first-touch is meaningless there.
 */
function isNativePlatform(): boolean {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
      .Capacitor;
    return cap?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Parse `utm_*` + referrer off the current page. Ignores empty and same-origin referrers. */
function readCurrentTouch(): FirstTouch {
  const touch: FirstTouch = {};

  const params = new URLSearchParams(window.location.search);
  for (const name of UTM_PARAMS) {
    const value = clean(params.get(name));
    if (value) touch[`first_${name}`] = value;
  }

  const referrer = clean(document.referrer);
  if (referrer) {
    try {
      const url = new URL(referrer);
      // An in-app navigation (or a reload) is not a referral source.
      if (url.origin !== window.location.origin) {
        // Origin + path only: a referrer's query/hash can carry anything.
        touch.first_referrer = clean(`${url.protocol}//${url.host}${url.pathname}`);
        const domain = clean(url.hostname);
        if (domain) touch.first_referring_domain = domain;
      }
    } catch {
      // Unparseable referrer — treat as absent.
    }
  }

  return touch;
}

/**
 * Remember this visit's campaign tags and referrer if — and only if — nothing
 * has been remembered yet. Call once at app start, BEFORE the router mounts,
 * while the original query string is still in the URL.
 *
 * The first visit is recorded even when it carries no tags (a direct visit
 * stores `{}`), so a later tagged visit cannot masquerade as the first touch.
 * No-op on native, without storage, or outside a browser.
 */
export function captureFirstTouch(): void {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (isNativePlatform()) return;
    const store = safeStorage();
    if (!store) return;
    if (store.getItem(FIRST_TOUCH_STORAGE_KEY) !== null) return;
    store.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(readCurrentTouch()));
  } catch {
    // Analytics is never load-bearing.
  }
}

/** The remembered first touch; `{}` when none, on native, or when storage is unreadable. */
export function getFirstTouch(): FirstTouch {
  try {
    if (isNativePlatform()) return {};
    const raw = safeStorage()?.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: FirstTouch = {};
    for (const key of FIRST_TOUCH_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      // Re-capped on read: the server rejects longer values with a 400, and a
      // hand-edited or older stored value must never block a signup.
      if (typeof value === 'string' && value) out[key] = value.slice(0, FIRST_TOUCH_MAX_LENGTH);
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Forget the remembered first touch. Called on logout (`resetIdentity`): by
 * then the tags are already on the person's profile, and leaving them would
 * hand one student's campaign to the next sign-up on a shared device.
 */
export function clearFirstTouch(): void {
  try {
    safeStorage()?.removeItem(FIRST_TOUCH_STORAGE_KEY);
  } catch {
    // Analytics is never load-bearing.
  }
}

/**
 * Spread into an account-creating request body: `{ first_touch }`, or `{}`
 * when there is nothing to send (native, direct visit, no storage).
 */
export function firstTouchBody(): { first_touch?: FirstTouch } {
  const first_touch = getFirstTouch();
  return Object.keys(first_touch).length > 0 ? { first_touch } : {};
}
